import { useNavigate } from "react-router-dom";
import { appPaths } from "../../Routes";
import {
    type AlertTitle,
    DriverAlertTitles,
    DriverDeclarationAlertTitles,
    LicenceCheckAlertTitles,
    GreyFleetAlertTitles,
} from "../../models/api/admin/dashboard";
import { type DriversListFiltersFormValues } from "../DriverListFilters/DriversListFiltersForm/DriversListFiltersFormValues";
import {
    FilterGreyFleetDto,
    NonAddMatchAnalysis,
    type FilterDeclarationsDto,
    type FilterLicenceChecksDto,
    type FilterLicenceInformationDto,
} from "../../models/app/admin/driverFilters";
import { driverListFiltersDefaultValues } from "../DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import { DeclarationType } from "../../models/app/eDeclarations";
import { errorToast } from "../../toast";
import {
    tableHeaderOptionsEnum,
    useTableHeaders,
} from "../../hooks/useTableHeaders";
import { driverAddressTableAllowedIdsEn } from "../ReusableComponents/SharedTableContent/driverAddressTableContent";
import { driverTableAllowedIdsEn } from "../DriverList";
import { match } from "ts-pattern";
import { driverVehicleTableAllowedIdsEn } from "../ReusableComponents/SharedTableContent/driverVehicleTableContent";
import { FilterType } from "../../models/app/admin/filter";
import { DriverCategoryOptionsType } from "../../utils/driverPageLocation";
import useTableStore from "../../stores/useTableStore";

export const useDashboardAlertActions = (
    selectedCompany: string | null,
    selectedDepartment: string | null
) => {
    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);
    const setScrollToTable = useTableStore((state) => state.setScrollToTable);
    const setIsRedirect = useTableStore((state) => state.setIsRedirect);
    const { updateTableConfig: updateDriverTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.allDrivers
    );
    const { updateTableConfig: updateProfDriverTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.professionalDrivers
    );
    const { updateTableConfig: updateGreyFleetTableConfig } = useTableHeaders(
        tableHeaderOptionsEnum.greyFleet
    );

    const navigate = useNavigate();

    const defaultFilterValues = driverListFiltersDefaultValues();
    const defaultProfessionalDriverFilterValues =
        driverListFiltersDefaultValues("professionalDrivers");

    const { licence: defaultProfessionalDriversLicenceValues } =
        defaultProfessionalDriverFilterValues;

    const companyDepartmentValues: Pick<
        DriversListFiltersFormValues,
        "company"
    > = {
        company: {
            ...(selectedCompany && { companyIds: [selectedCompany] }),
            ...(selectedDepartment && {
                departmentIds: [selectedDepartment],
            }),
        },
    };

    const handleApplyFilter = <AllowedIds extends string>(
        location: DriverCategoryOptionsType,
        filter: FilterType,
        columns?: AllowedIds[]
    ) => {
        setAppliedFilter(filter, location);
        let targetPath = undefined;

        match(location)
            .with("drivers", () => {
                if (columns) {
                    updateDriverTableConfig(columns);
                }
                setScrollToTable(true);
                targetPath = appPaths.adminDrivers;
            })
            .with("professionalDrivers", () => {
                if (columns) {
                    updateProfDriverTableConfig(columns);
                }
                setScrollToTable(true);
                targetPath = appPaths.adminProfessionalDrivers;
            })
            .with("greyfleet", () => {
                if (columns) {
                    updateGreyFleetTableConfig(columns);
                }
                setScrollToTable(true);
                targetPath = appPaths.adminGreyFleet;
            })
            .exhaustive();

        if (targetPath) {
            setIsRedirect(true);
            navigate(targetPath);
        }
    };

    const setDriverAnalysisBoolean = (key: keyof NonAddMatchAnalysis) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultFilterValues,
                ...companyDepartmentValues,
                driverAnalysis: {
                    nonAddMatch: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter("drivers", filter, [
            driverAddressTableAllowedIdsEn.addressMatch,
        ]);
    };

    const setProfessionalDriverAnalysisBoolean = (
        key: keyof NonAddMatchAnalysis
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultProfessionalDriverFilterValues,
                ...companyDepartmentValues,
                driverAnalysis: {
                    nonAddMatch: {
                        [key]: true,
                    },
                },
            },
        };

        handleApplyFilter("professionalDrivers", filter, [
            driverAddressTableAllowedIdsEn.addressMatch,
        ]);
    };

    const setDriverLicenceBoolean = (
        key: keyof FilterLicenceInformationDto
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultFilterValues,
                ...companyDepartmentValues,
                licence: {
                    [key]: true,
                },
            },
        };

        /** non-exhaustive */
        match(key)
            .with(
                "isExpired",
                "hasExpiredPhotocard",
                "hasExpiringPhotocard",
                () =>
                    handleApplyFilter("drivers", filter, [
                        driverTableAllowedIdsEn.licenceExpires,
                        driverTableAllowedIdsEn.photoCardExpiryEmailLastSent,
                    ])
            )
            .with("hasExpiredEntitlements", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.expiredCategories,
                    driverTableAllowedIdsEn.categoryExpiryDates,
                    driverTableAllowedIdsEn.entitlementExpiryEmailLastSent,
                ])
            )
            .with("hasExpiringEntitlements", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.expiringCategories,
                    driverTableAllowedIdsEn.categoryExpiryDates,
                    driverTableAllowedIdsEn.entitlementExpiryEmailLastSent,
                ])
            )
            .with("hasExpiredCpc", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.cpcLgvExpiryDate,
                    driverTableAllowedIdsEn.cpcPcvExpiryDate,
                ])
            )
            .with("hasExpiredTacho", () =>
                handleApplyFilter("drivers", filter, [                                    
                    driverTableAllowedIdsEn.tachoCardExpiryDate,
                ])
            )
            .with("hasExpiringCpc", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.cpcLgvExpiryDate,
                    driverTableAllowedIdsEn.cpcPcvExpiryDate,
                ])
            )
            .with("hasExpiringTacho", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.tachoCardExpiryDate,
                ])
            )
            .otherwise(() => handleApplyFilter("drivers", filter));
    };

    const setProfessionalDriverLicenceBoolean = (
        key: keyof FilterLicenceInformationDto
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultProfessionalDriverFilterValues,
                ...companyDepartmentValues,
                licence: {
                    ...defaultProfessionalDriversLicenceValues,
                    [key]: true,
                },
            },
        };

        /** non-exhaustive */
        match(key)
            .with(
                "isExpired",
                "hasExpiredPhotocard",
                "hasExpiringPhotocard",
                () =>
                    handleApplyFilter("professionalDrivers", filter, [
                        driverTableAllowedIdsEn.licenceExpires,
                        driverTableAllowedIdsEn.photoCardExpiryEmailLastSent,
                    ])
            )
            .with("hasExpiredEntitlements", () =>
                handleApplyFilter("professionalDrivers", filter, [
                    driverTableAllowedIdsEn.expiredCategories,
                    driverTableAllowedIdsEn.categoryExpiryDates,
                    driverTableAllowedIdsEn.entitlementExpiryEmailLastSent,
                ])
            )
            .with("hasExpiringEntitlements", () =>
                handleApplyFilter("professionalDrivers", filter, [
                    driverTableAllowedIdsEn.expiringCategories,
                    driverTableAllowedIdsEn.categoryExpiryDates,
                    driverTableAllowedIdsEn.entitlementExpiryEmailLastSent,
                ])
            )
            .with("hasExpiredCpc", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.cpcLgvExpiryDate,
                    driverTableAllowedIdsEn.cpcPcvExpiryDate,
                ])
            )
            .with("hasExpiredTacho", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.tachoCardExpiryDate,
                ])
            )
            .with("hasExpiringCpc", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.cpcLgvExpiryDate,
                    driverTableAllowedIdsEn.cpcPcvExpiryDate,
                ])
            )
            .with("hasExpiringTacho", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.tachoCardExpiryDate,
                ])
            )
            .otherwise(() => handleApplyFilter("professionalDrivers", filter));
    };

    type FilterDeclarationsKey = keyof FilterDeclarationsDto;
    // array of key/values here since some filters require more than one
    const setDeclarationBoolean = (
        entries: { key: FilterDeclarationsKey; value: boolean }[],
        type?: DeclarationType
    ) => {
        const keysObject = Object.fromEntries(
            entries.map(({ key, value }) => [key, value])
        );
        const declarationType = type ? { declarationTypes: [type] } : null;

        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultFilterValues,
                ...companyDepartmentValues,
                declarationsQuery: {
                    ...declarationType,
                    ...keysObject,
                },
            },
        };

        handleApplyFilter("drivers", filter, [
            driverTableAllowedIdsEn.declarationExpiry,
            driverTableAllowedIdsEn.declarationStatus,
        ]);
    };

    const setLicenceCheckBoolean = (key: keyof FilterLicenceChecksDto) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultFilterValues,
                ...companyDepartmentValues,
                dvlaQuery: {
                    [key]: true,
                },
            },
        };

        /** non-exhaustive */
        match(key)
            .with("hasCheckDueThisMonth", "hasCheckScheduled", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.licenceNextCheckDate,
                ])
            )
            .with("hasFailedChecks", () =>
                handleApplyFilter("drivers", filter, [
                    driverTableAllowedIdsEn.previousLicenceChecks,
                ])
            )
            .otherwise(() => handleApplyFilter("drivers", filter));
    };

    const setGreyFleetBoolean = (
        key: keyof FilterGreyFleetDto,
        value?: boolean
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...defaultFilterValues,
                ...companyDepartmentValues,
                greyFleet: true,
                greyFleetQuery: {
                    [key]: value ?? true,
                },
            },
        };

        /** non-exhaustive */
        match(key)
            .with("hasExpiringMot", "hasMotExpired", () =>
                handleApplyFilter("greyfleet", filter, [
                    driverVehicleTableAllowedIdsEn.motExpiry,
                ])
            )
            .with("hasExpiringRoadTax", "hasRoadTaxExpired", () =>
                handleApplyFilter("greyfleet", filter, [
                    driverVehicleTableAllowedIdsEn.roadTaxExpiry,
                ])
            )
            .with("hasPolicyWithoutBusinessCover", () =>
                handleApplyFilter("greyfleet", filter, [
                    driverVehicleTableAllowedIdsEn.businessCover,
                ])
            )
            .with(
                "hasInsuranceUnverified",
                "hasInsuranceCertificateToUpload",
                () =>
                    handleApplyFilter("greyfleet", filter, [
                        driverVehicleTableAllowedIdsEn.insuranceType,
                    ])
            )
            .with("hasExpiringInsurancePolicy", () =>
                handleApplyFilter("greyfleet", filter, [
                    driverVehicleTableAllowedIdsEn.vehicleInsuranceExpiry,
                ])
            )
            .with("hasInsurancePolicyExpired", () =>
                handleApplyFilter("greyfleet", filter, [
                    driverVehicleTableAllowedIdsEn.vehicleInsuranceExpiry,
                ])
            )
            .otherwise(() => handleApplyFilter("greyfleet", filter));
    };

    const driverAlertAction = (alertTitle: AlertTitle) => {
        switch (alertTitle) {
            case DriverAlertTitles.CpcExpired:
                setDriverLicenceBoolean("hasExpiredCpc");
                break;
            case DriverAlertTitles.HasExpiringCpc:
                setDriverLicenceBoolean("hasExpiringCpc");
                break;
            case DriverAlertTitles.EntitlementExpired:
                setDriverLicenceBoolean("hasExpiredEntitlements");
                break;
            case DriverAlertTitles.HasExpiringEntitlement:
                setDriverLicenceBoolean("hasExpiringEntitlements");
                break;
            case DriverAlertTitles.TachocardExpired:
                setDriverLicenceBoolean("hasExpiredTacho");
                break;
            case DriverAlertTitles.HasExpiringTachocard:
                setDriverLicenceBoolean("hasExpiringTacho");
                break;
            case DriverAlertTitles.PhotocardExpired:
                setDriverLicenceBoolean("hasExpiredPhotocard");
                break;
            case DriverAlertTitles.HasExpiringPhotocard:
                setDriverLicenceBoolean("hasExpiringPhotocard");
                break;
            case DriverAlertTitles.AddressNonmatch:
                setDriverAnalysisBoolean("nonAddressMatches");
                break;
            default:
                errorToast(`Unhandled alert title: ${alertTitle}`);
                break;
        }
    };

    const professionalDriverAlertAction = (alertTitle: AlertTitle) => {
        switch (alertTitle) {
            case DriverAlertTitles.CpcExpired:
                setProfessionalDriverLicenceBoolean("hasExpiredCpc");
                break;
            case DriverAlertTitles.HasExpiringCpc:
                setProfessionalDriverLicenceBoolean("hasExpiringCpc");
                break;
            case DriverAlertTitles.EntitlementExpired:
                setProfessionalDriverLicenceBoolean("hasExpiredEntitlements");
                break;
            case DriverAlertTitles.HasExpiringEntitlement:
                setProfessionalDriverLicenceBoolean("hasExpiringEntitlements");
                break;
            case DriverAlertTitles.TachocardExpired:
                setProfessionalDriverLicenceBoolean("hasExpiredTacho");
                break;
            case DriverAlertTitles.HasExpiringTachocard:
                setProfessionalDriverLicenceBoolean("hasExpiringTacho");
                break;
            case DriverAlertTitles.PhotocardExpired:
                setProfessionalDriverLicenceBoolean("hasExpiredPhotocard");
                break;
            case DriverAlertTitles.HasExpiringPhotocard:
                setProfessionalDriverLicenceBoolean("hasExpiringPhotocard");
                break;
            case DriverAlertTitles.AddressNonmatch:
                setProfessionalDriverAnalysisBoolean("nonAddressMatches");
                break;
            default:
                errorToast(`Unhandled alert title: ${alertTitle}`);
                break;
        }
    };

    const driverDeclarationAlertAction = (alertTitle: AlertTitle) => {
        switch (alertTitle) {
            case DriverDeclarationAlertTitles.ExpiredDeclarations:
                setDeclarationBoolean([
                    { key: "hasExpiredDeclaration", value: true },
                ]);
                break;
            case DriverDeclarationAlertTitles.ExpiringDeclarations:
                setDeclarationBoolean([
                    { key: "hasExpiringDeclaration", value: true },
                ]);
                break;
            case DriverDeclarationAlertTitles.PendingDeclarations:
                setDeclarationBoolean(
                    [{ key: "hasIncompleteDeclaration", value: true }],
                    DeclarationType.EDeclaration
                );
                break;
            case DriverDeclarationAlertTitles.PaperDeclarationsRequested:
                setDeclarationBoolean(
                    [{ key: "hasRequestedDeclaration", value: true }],
                    DeclarationType.PaperDeclaration
                );
                break;
            case DriverDeclarationAlertTitles.PaperDeclarationsNotRequested:
                setDeclarationBoolean(
                    [{ key: "hasNotRequestedDeclaration", value: true }],
                    DeclarationType.PaperDeclaration
                );
                break;
            default:
                errorToast(`Unhandled alert title: ${alertTitle}`);
                break;
        }
    };

    const licenceCheckAlertAction = (alertTitle: AlertTitle) => {
        switch (alertTitle) {
            case LicenceCheckAlertTitles.InvalidLicences:
                setLicenceCheckBoolean("hasInvalidLicence");
                break;
            case LicenceCheckAlertTitles.FailedChecks:
                setLicenceCheckBoolean("hasFailedChecks");
                break;
            case LicenceCheckAlertTitles.ChecksDue:
                setLicenceCheckBoolean("hasCheckDueThisMonth");
                break;
            case LicenceCheckAlertTitles.ChecksScheduled:
                setLicenceCheckBoolean("hasCheckScheduled");
                break;
        }
    };

    const greyFleetAlertAction = (alertTitle: AlertTitle) => {
        switch (alertTitle) {
            case GreyFleetAlertTitles.hasCompletedInvite:
                setGreyFleetBoolean("hasCompletedInvite", false);
                break;
            case GreyFleetAlertTitles.hasExpiringMot:
                setGreyFleetBoolean("hasExpiringMot");
                break;
            case GreyFleetAlertTitles.hasMotExpired:
                setGreyFleetBoolean("hasMotExpired");
                break;
            case GreyFleetAlertTitles.hasExpiringRoadTax:
                setGreyFleetBoolean("hasExpiringRoadTax");
                break;
            case GreyFleetAlertTitles.hasRoadTaxExpired:
                setGreyFleetBoolean("hasRoadTaxExpired");
                break;
            case GreyFleetAlertTitles.hasExpiringInsurancePolicy:
                setGreyFleetBoolean("hasExpiringInsurancePolicy");
                break;
            case GreyFleetAlertTitles.hasInsurancePolicyExpired:
                setGreyFleetBoolean("hasInsurancePolicyExpired");
                break;
            case GreyFleetAlertTitles.hasInsuranceToUpload:
                setGreyFleetBoolean("hasInsuranceCertificateToUpload");
                break;
            case GreyFleetAlertTitles.hasUnverifiedInsurance:
                setGreyFleetBoolean("hasInsuranceUnverified");
                break;
            case GreyFleetAlertTitles.hasBreakdownCoverExpiring:
                setGreyFleetBoolean("hasBreakdownCoverExpiring");
                break;
            case GreyFleetAlertTitles.hasNoOrExpiredBreakdownCover:
                setGreyFleetBoolean("hasNoOrExpiredBreakdownCover");
                break;
            default:
                errorToast(`Unhandled alert title: ${alertTitle}`);
                break;
        }
    };

    return {
        driverAlertAction,
        driverDeclarationAlertAction,
        licenceCheckAlertAction,
        greyFleetAlertAction,
        professionalDriverAlertAction,
    };
};
